import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShieldCheckIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  CheckCircleIcon,
  XCircleIcon,
  GlobeAltIcon,
  ScaleIcon,
  UserGroupIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';

// Import the services we updated earlier
import { getAllPolicies, acknowledgePolicy, getPolicyStats } from '../services/employee.service';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

// Icon mapping helper for dynamic policies from API
const getIcon = (title = "") => {
  const t = title.toLowerCase();
  if (t.includes('security') || t.includes('data')) return ShieldCheckIcon;
  if (t.includes('code') || t.includes('conduct') || t.includes('ethics')) return ScaleIcon;
  if (t.includes('nda') || t.includes('confidential')) return DocumentTextIcon;
  if (t.includes('portal') || t.includes('terms')) return GlobeAltIcon;
  return UserGroupIcon;
};

const PolicyAcknowledgement = () => {
  const [policies, setPolicies] = useState([]);
  const [stats, setStats] = useState({ acknowledgedCount: 0, pendingCount: 0, totalPolicies: 0 });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPolicy, setCurrentPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // --- API INTEGRATION: FETCH DATA ---
const fetchPolicyData = async () => {
  try {
    setLoading(true);

    const role = (localStorage.getItem("role") || "").toUpperCase();

    // Always load policies
    const policiesList = await getAllPolicies();
    
    // 🔥 NORMALIZE SQL ROWS TO OBJECTS
    const normalizedPolicies = (Array.isArray(policiesList) ? policiesList : []).map(
      (row) => ({
        id: row[0],                 // 🔥 DB ID (THIS FIXES EVERYTHING)
        policy_code: row[1],
        title: row[2],
        category: row[3],
        applies_to: row[4],
        document_path: row[5],
        description: row[6],
        created_at: row[7],
        updated_at: row[8],
        deleted: row[9],
        version: row[10],
        status: row[11],
        is_acknowledged: row[12] || false, // 🔥 ACKNOWLEDGMENT STATUS FROM DB
        acknowledged_at: row[13] // 🔥 ACKNOWLEDGMENT DATE
      })
    );

    console.log("🔍 NORMALIZED POLICIES:", normalizedPolicies); // Debug log
    setPolicies(normalizedPolicies);

    // 🔥 CALCULATE STATS FROM USER'S POLICIES
    const acknowledgedCount = normalizedPolicies.filter(p => p.is_acknowledged).length;
    const pendingCount = normalizedPolicies.filter(p => !p.is_acknowledged).length;
    const totalPolicies = normalizedPolicies.length;

    // 🔥 USE CALCULATED STATS FOR ALL ROLES
    setStats({
      acknowledgedCount,
      pendingCount,
      totalPolicies,
    });

    setError(null);
  } catch (err) {
    console.error("Policy Fetch Error:", err);
    setError(typeof err === 'string' ? err : 'Failed to load policies');
  } finally {
    setLoading(false);
  }
};
  useEffect(() => {
    fetchPolicyData();
  }, []);

  const handleOpenModal = (policy) => {
    console.log("Opening modal for policy:", policy); // Debug log
    setCurrentPolicy(policy);
    setIsModalOpen(true);
  };

  // --- API INTEGRATION: SUBMIT ACKNOWLEDGEMENT ---
  const handleSubmitSignature = async (signatureValue) => {
    if (!currentPolicy) {
      console.error("❌ currentPolicy is null");
      return;
    }

    console.log("🧾 CURRENT POLICY:", currentPolicy);

    const payload = {
      policy_id: currentPolicy.id, // 🔥 THIS MUST EXIST
      e_signature: signatureValue.trim()
    };

    console.log("📤 ACK PAYLOAD:", payload);

    try {
      setSubmitting(true);
      await acknowledgePolicy(payload);
      await fetchPolicyData();
      setIsModalOpen(false);
      setCurrentPolicy(null);
    } catch (err) {
      console.error("❌ Acknowledge failed:", err);
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Stats calculation for UI
  const progressPercentage = stats.totalPolicies === 0 
    ? 0 
    : Math.round((stats.acknowledgedCount / stats.totalPolicies) * 100);

  const pieData = {
    labels: ['Completed', 'Pending'],
    datasets: [{
      data: [stats.acknowledgedCount, stats.pendingCount],
      backgroundColor: ['#22c55e', '#ef4444'],
      hoverBackgroundColor: ['#16a34a', '#dc2626'],
    }],
  };

  const barData = {
    labels: ['Completed', 'Pending', 'Total'],
    datasets: [{
      label: 'Policies',
      data: [stats.acknowledgedCount, stats.pendingCount, stats.totalPolicies],
      backgroundColor: ['#22c55e', '#ef4444', '#3b82f6'],
    }],
  };

  const appStyle = { fontFamily: 'Poppins, Roboto, sans-serif' };

  // Get role for conditional rendering
  const role = (localStorage.getItem("role") || "").toUpperCase();

  // --- SUB-COMPONENT: POLICY CARD ---
  const PolicyCard = ({ policy }) => {
    console.log("📋 POLICY CARD RECEIVED:", policy); // Debug log
    const isAcknowledged = policy.is_acknowledged; // Assumes API returns this boolean
    const IconComponent = getIcon(policy.title);

    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className={`bg-white rounded-xl border ${isAcknowledged ? 'border-green-200' : 'border-gray-200'} p-5 hover:shadow-md transition-all`}
      >
        <div className="flex justify-between items-start mb-3">
          <div className={`p-2 rounded-lg ${isAcknowledged ? 'bg-green-50' : 'bg-indigo-50'}`}>
            <IconComponent className={`h-7 w-7 ${isAcknowledged ? 'text-green-600' : 'text-indigo-600'}`} />
          </div>
          <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${isAcknowledged ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
            {isAcknowledged ? 'Completed' : 'Pending'}
          </span>
        </div>
        <h3 className="font-semibold text-gray-900 text-[15px] mb-1.5">{policy.title}</h3>
        <p className="text-sm text-gray-600 line-clamp-2 min-h-[44px]">{policy.description}</p>
        <div className="mt-4">
          {isAcknowledged ? (
            <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded-lg border border-gray-200">
              Acknowledged on: <strong>{new Date(policy.acknowledged_at).toLocaleDateString()}</strong>
            </div>
          ) : (
            <button
              onClick={() => handleOpenModal(policy)}
              className="w-full py-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white font-medium rounded-lg text-sm flex items-center justify-center gap-2"
            >
              <PencilSquareIcon className="h-4 w-4" />
              Review & Sign
            </button>
          )}
        </div>
      </motion.div>
    );
  };

  // --- SUB-COMPONENT: MODAL ---
  const SignatureModal = ({ policy, isOpen, onClose, onSubmit }) => {
    const [localSignatureText, setLocalSignatureText] = useState('');
    const [modalError, setModalError] = useState(null);

    const handleSubmission = () => {
      if (!localSignatureText.trim()) {
        setModalError("Please type your full name to acknowledge.");
        return;
      }
      onSubmit(localSignatureText);
    };

    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden">
          <div className="p-5 border-b bg-gradient-to-r from-indigo-50 to-purple-50 flex justify-between items-center">
            <h3 className="text-xl font-semibold text-indigo-800">{policy?.title}</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-red-600"><XCircleIcon className="h-8 w-8" /></button>
          </div>
          <div className="p-6 overflow-y-auto flex-1 prose prose-sm max-w-none">
             <div dangerouslySetInnerHTML={{ __html: policy?.content || '' }} />
          </div>
          <div className="p-6 border-t bg-gray-50">
            <p className="text-sm text-amber-800 bg-amber-50 p-3 rounded-lg border border-amber-200 mb-4">
              **Mandatory:** Type your full legal name to acknowledge you have read and agree to this policy
            </p>
            <input
              type="text"
              value={localSignatureText}
              onChange={(e) => setLocalSignatureText(e.target.value)}
              placeholder="Your Full Legal Name"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
            />
            {modalError && <p className="text-red-600 text-sm mt-2">{modalError}</p>}
            <div className="mt-6 flex justify-end gap-4">
              <button onClick={onClose} className="px-6 py-2 bg-white border border-gray-300 rounded-xl hover:bg-gray-50">Cancel</button>
              <button
                onClick={handleSubmission}
                disabled={submitting || !localSignatureText.trim()}
                className="px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl flex items-center gap-2 disabled:opacity-50"
              >
                {submitting ? <ArrowPathIcon className="h-5 w-5 animate-spin" /> : <CheckCircleIcon className="h-5 w-5" />}
                {submitting ? 'Signing...' : 'Confirm & Sign'}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center text-indigo-600" style={appStyle}>
      <ArrowPathIcon className="h-8 w-8 animate-spin mr-2" /> Loading Policies...
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50/70 py-8 px-4 sm:px-6 lg:px-8" style={appStyle}>
      <div className="max-w-7xl mx-auto">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex justify-between items-center">
            <span>{typeof error === 'string' ? error : 'An error occurred while loading policies.'}</span>
            <button onClick={() => setError(null)}><XCircleIcon className="h-5 w-5" /></button>
          </div>
        )}

        <div className="mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Policy Compliance</h1>
          <p className="text-gray-600 mb-6">Review and acknowledge mandatory company policies</p>
          
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 flex flex-col sm:flex-row items-center gap-8">
            <div className="relative w-40 h-40">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 140 140">
                <circle cx="70" cy="70" r="62" stroke="#e5e7eb" strokeWidth="14" fill="none" />
                <circle cx="70" cy="70" r="62" stroke="url(#progress)" strokeWidth="14" fill="none"
                  strokeDasharray={Math.PI * 124}
                  strokeDashoffset={Math.PI * 124 * (1 - progressPercentage / 100)}
                  strokeLinecap="round" />
                <defs><linearGradient id="progress"><stop offset="0%" stopColor="#6366f1" /><stop offset="100%" stopColor="#a855f7" /></linearGradient></defs>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-4xl font-bold text-indigo-700">{progressPercentage}%</span>
                <span className="text-sm text-gray-500">Compliance</span>
              </div>
            </div>
            <div className="flex-1 grid grid-cols-3 gap-6 text-center">
              <div><p className="text-3xl font-bold text-indigo-600">{stats.acknowledgedCount}</p><p className="text-sm text-gray-600">Completed</p></div>
              <div><p className="text-3xl font-bold text-orange-600">{stats.pendingCount}</p><p className="text-sm text-gray-600">Pending</p></div>
              <div><p className="text-3xl font-bold text-gray-900">{stats.totalPolicies}</p><p className="text-sm text-gray-600">Total</p></div>
            </div>
          </div>

          {(role === "ADMIN" || role === "MANAGER") && (
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="h-72"><h2 className="text-lg font-semibold mb-4">Status Distribution</h2><Pie data={pieData} options={{ maintainAspectRatio: false }} /></div>
              <div className="h-72"><h2 className="text-lg font-semibold mb-4">Policy Metrics</h2><Bar data={barData} options={{ maintainAspectRatio: false }} /></div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence>
            {Array.isArray(policies) && policies.map((policy) => (
              <PolicyCard key={policy.id} policy={policy} />
            ))}
          </AnimatePresence>
        </div>

        <SignatureModal
          policy={currentPolicy}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleSubmitSignature}
        />
      </div>
    </div>
  );
};

export default PolicyAcknowledgement;